import { EventEmitter } from 'events';

class PrinterWebSocketService extends EventEmitter {
  constructor() {
    super();
    this.ws = null;
    this.url = process.env.REACT_APP_PRINTER_WS_URL;
    if (!this.url) {
      console.error('❌ ERROR: REACT_APP_PRINTER_WS_URL no está definida');
      this.url = 'wss://tibo-printer-service.fly.dev';
    }
    if (!this.url.startsWith('ws://') && !this.url.startsWith('wss://')) {
      console.error('❌ ERROR: URL del WebSocket inválida:', this.url);
      this.url = 'wss://tibo-printer-service.fly.dev';
    }
    console.log('=== DEBUG PRINTER SERVICE INIT ===');
    console.log('URL configurada:', this.url);
    console.log('Protocolo:', this.url?.split('://')[0]);
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectTimeout = null;
    this.businessId = null;
    this.connectionStatus = 'disconnected'; // 'disconnected' | 'connecting' | 'connected' | 'error'
  }

  connect(businessId) {
    console.log('=== DEBUG PRINTER WEBSOCKET SERVICE ===');
    console.log('Intentando conectar con:', {
      url: this.url,
      businessId: businessId,
      currentStatus: this.connectionStatus,
      protocol: window.location.protocol
    });

    if (!businessId) {
      console.warn('⚠️ No hay business_id disponible para conectar al Printer Service');
      this.connectionStatus = 'error';
      this.emit('statusChange', this.connectionStatus);
      this.emit('error', new Error('Se requiere un negocio activo para usar las impresoras'));
      return;
    }

    if (this.connectionStatus === 'connecting') {
      console.log('⏳ Ya intentando conectar...');
      return;
    }

    this.businessId = businessId;
    this.connectionStatus = 'connecting';
    this.emit('statusChange', this.connectionStatus);

    try {
      console.log('🔌 Creando WebSocket...');
      this.ws = new WebSocket(this.url);
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('❌ Error creando WebSocket:', error);
      this.handleError(error);
    }
  }

  handleOpen() {
    console.log('=== DEBUG WEBSOCKET OPEN ===');
    console.log('🔌 Conectado al Printer Service');
    console.log('Business ID:', this.businessId);
    
    this.reconnectAttempts = 0;
    this.connectionStatus = 'connected';
    this.emit('statusChange', this.connectionStatus);
    
    // Registrarse como cliente React
    console.log('📝 Enviando registro:', {
      type: 'register',
      printerId: `react-${this.businessId}`,
      clientType: 'react',
      businessId: this.businessId
    });

    this.send({
      type: 'register',
      printerId: `react-${this.businessId}`,
      clientType: 'react',
      businessId: this.businessId
    });

    // Verificar estado del printer client
    this.send({
      type: 'check_client_status',
      businessId: this.businessId
    });
  }

  handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      console.log('📥 Mensaje recibido:', message);

      switch (message.type) {
        case 'client_status':
          this.emit('clientStatus', message.status);
          break;
        case 'printer_found':
          this.emit('printerFound', message.printer);
          break;
        case 'printer_status':
          this.emit('printerStatus', message.printer);
          break;
        case 'print_job_status':
          this.emit('printJobStatus', message.job);
          break;
        case 'error':
          this.emit('error', message.error);
          break;
        default:
          console.log('Mensaje no manejado:', message);
      }
    } catch (error) {
      console.error('Error parseando mensaje:', error);
    }
  }

  handleError(error) {
    console.error('❌ Error WS:', error);
    console.error('Estado al momento del error:', {
      url: this.url,
      businessId: this.businessId,
      readyState: this.ws?.readyState,
      protocol: this.url?.split('://')[0]
    });
    this.connectionStatus = 'error';
    this.emit('statusChange', this.connectionStatus);
    this.emit('error', error);
  }

  handleClose() {
    console.log('WebSocket cerrado');
    this.connectionStatus = 'disconnected';
    this.emit('statusChange', this.connectionStatus);

    // Intentar reconectar si no excedimos el límite
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectAttempts++;
        console.log(`Intento de reconexión ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
        this.connect(this.businessId);
      }, 5000 * Math.pow(2, this.reconnectAttempts)); // Backoff exponencial
    }
  }

  send(data) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.error('WebSocket no está conectado');
      throw new Error('WebSocket no está conectado');
    }
  }

  // Comandos específicos
  async sendPrintCommand(printerId, content, type = 'receipt') {
    try {
      this.send({
        type: 'print',
        printerId,
        content,
        jobType: type
      });
    } catch (error) {
      console.error('Error enviando comando de impresión:', error);
      throw error;
    }
  }

  async testPrinter(printerId) {
    try {
      this.send({
        type: 'test_printer',
        printerId
      });
    } catch (error) {
      console.error('Error enviando test de impresora:', error);
      throw error;
    }
  }

  async scanNetwork() {
    try {
      this.send({
        type: 'scan_network'
      });
    } catch (error) {
      console.error('Error iniciando escaneo de red:', error);
      throw error;
    }
  }

  async checkClientStatus() {
    try {
      this.send({
        type: 'check_client_status'
      });
    } catch (error) {
      console.error('Error verificando estado del cliente:', error);
      throw error;
    }
  }

  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.businessId = null;
    this.reconnectAttempts = 0;
    this.connectionStatus = 'disconnected';
    this.emit('statusChange', this.connectionStatus);
  }

  getStatus() {
    return this.connectionStatus;
  }
}

// Singleton instance
export const printerWebSocket = new PrinterWebSocketService(); 